import { useEffect, useState, useCallback } from "react";
import {
  listInquiries,
  getUnreadInquiryIds,
  markInquiryRead,
  deleteInquiry,
  syncReservations,
} from "../eventsApi";
import { LeadCard } from "../components/LeadCard";
import { LeadDetail } from "../components/LeadDetail";
import { AddInquiryModal } from "../components/AddInquiryModal";
import type { Inquiry, InquiryStatus } from "../types";
import { STATUS_LABELS } from "../types";

const PIPELINE_COLUMNS: InquiryStatus[] = [
  "new",
  "contacted",
  "quoted",
  "deposit_paid",
  "completed",
  "lost",
  "cancelled",
];
// ReservationSystem #484: paid in full (confirmed) shows under Deposit Paid - Confirmed,
// so no inquiry drops off the pipeline.
const inColumn = (column: InquiryStatus, status: InquiryStatus) => status === column || (column === "deposit_paid" && status === "confirmed");

export function PipelineView() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [res, unread] = await Promise.all([
        listInquiries({ limit: 100 }),
        getUnreadInquiryIds(),
      ]);
      setInquiries(res.data);
      setUnreadIds(new Set(unread.data));
    } catch (err) {
      console.error("Failed to load inquiries", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // The Reservation System answers inline. A browser reload only re-reads HQ,
  // so this button is how staff skip the hourly poll.
  const handleSync = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      await syncReservations();
      await fetchData();
      setSyncNote(`Synced ${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`);
    } catch (err) {
      console.error("Status sync failed", err);
      setSyncNote("Sync failed — try again");
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (inquiry: Inquiry) => {
    const confirmed = window.confirm(
      `Delete "${inquiry.first_name} ${inquiry.last_name}"? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await deleteInquiry(inquiry.id);
      setInquiries((prev) => prev.filter((i) => i.id !== inquiry.id));
      if (selected?.id === inquiry.id) setSelected(null);
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete inquiry. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        Loading pipeline…
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-end gap-3 border-b border-tribal-border px-4 py-2">
          {syncNote && <span className="text-xs text-tribal-muted">{syncNote}</span>}
          <button
            onClick={handleSync}
            disabled={syncing}
            title="Pull deposit, payment and cancellation status from the Reservation System now instead of waiting for the hourly sync"
            className="rounded bg-tribal-red/20 px-3 py-1 text-xs text-tribal-red hover:bg-tribal-red/30 disabled:opacity-50"
          >
            {syncing ? "Syncing…" : "Sync status"}
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="rounded bg-tribal-red px-3 py-1 text-xs font-medium text-white hover:bg-tribal-red-dark transition-colors"
          >
            + Add Event
          </button>
        </div>
        <div className="flex flex-1 gap-3 overflow-x-auto p-4">
          {PIPELINE_COLUMNS.map((status) => {
            const cards = inquiries.filter((i) => inColumn(status, i.status));
            return (
              <div
                key={status}
                className="flex w-64 flex-shrink-0 flex-col rounded-lg border border-tribal-border bg-tribal-bg"
              >
                <div className="flex items-center justify-between border-b border-tribal-border px-3 py-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted">
                    {STATUS_LABELS[status]}
                  </h3>
                  <span className="rounded-full bg-tribal-border px-2 py-0.5 text-xs text-white">
                    {cards.length}
                  </span>
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto p-2">
                  {cards.map((inquiry) => (
                    <LeadCard
                      key={inquiry.id}
                      inquiry={inquiry}
                      hasUnread={unreadIds.has(inquiry.id)}
                      onClick={() => {
                        setSelected(inquiry);
                        if (unreadIds.has(inquiry.id)) {
                          setUnreadIds((prev) => {
                            const next = new Set(prev);
                            next.delete(inquiry.id);
                            return next;
                          });
                          markInquiryRead(inquiry.id).catch(() => {});
                        }
                      }}
                      {...(status === "cancelled" ? { onDelete: () => handleDelete(inquiry) } : {})}
                    />
                  ))}
                  {cards.length === 0 && (
                    <p className="py-4 text-center text-xs text-tribal-muted">No leads</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <LeadDetail
          inquiry={selected}
          onClose={() => setSelected(null)}
          onUpdate={(updated) => {
            setInquiries((prev) =>
              prev.map((i) => (i.id === updated.id ? updated : i))
            );
            setSelected(updated);
          }}
        />
      )}

      {showAddModal && (
        <AddInquiryModal
          onClose={() => setShowAddModal(false)}
          onCreated={(inquiry) => {
            setInquiries((prev) => [inquiry, ...prev]);
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}
